import { DUPLICATE_OVERRIDE_REASON_MIN_LENGTH, type DuplicateReason } from '@wodi/domain';
import type { DuplicateCheckResponse } from '@wodi/shared';
import { Button, Dialog, DialogActions, TextArea } from '@wodi/ui';
import { useState } from 'react';

import { PatientResultRow } from './patient-result-row.tsx';
import styles from './patients.module.css';

export const DUPLICATE_REASON_TEXT: Record<DuplicateReason, string> = {
  same_phone: 'Shares a phone number',
  similar_name_same_birth_date: 'Similar name and the same date of birth',
  similar_name_close_birth_date: 'Similar name and a close date of birth (one is estimated)',
  same_name_same_principal: 'Already a dependant of the same person, with a similar name',
};

type Candidate = DuplicateCheckResponse['candidates'][number];

/**
 * Possible duplicates (plan §4.2, registration.md scenario 4). Each candidate says what
 * matched, in words. The receptionist either uses one of them or says this is a different
 * person, with a reason that is stored and audited.
 */
export function DuplicateDialog({
  candidates,
  isOpen,
  onOpenChange,
  onUse,
  onDifferent,
  isPending,
}: {
  candidates: readonly Candidate[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onUse: (patientId: string) => void;
  onDifferent: (reason: string) => void;
  isPending: boolean;
}) {
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const tooShort = reason.trim().length < DUPLICATE_OVERRIDE_REASON_MIN_LENGTH;
  return (
    <Dialog
      title={
        candidates.length === 1
          ? 'This patient may already be registered'
          : `This patient may already be registered (${String(candidates.length)} possible matches)`
      }
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      <p>Check each possible match with the patient before registering someone new.</p>
      <ul className={styles.candidateList}>
        {candidates.map((candidate) => (
          <li key={candidate.patient.id} className={styles.candidate}>
            <PatientResultRow result={candidate.patient} />
            <ul className={styles.reasonList}>
              {candidate.reasons.map((r) => (
                <li key={r}>{DUPLICATE_REASON_TEXT[r]}</li>
              ))}
            </ul>
            <Button
              dense
              aria-label={`Use this patient: ${candidate.patient.displayName}, ${candidate.patient.patientNumber}`}
              onPress={() => {
                onUse(candidate.patient.id);
              }}
            >
              Use this patient
            </Button>
          </li>
        ))}
      </ul>
      <TextArea
        label="Why is this a different person?"
        description="For example: twins, or a shared family phone. Stored with the registration."
        value={reason}
        onChange={setReason}
        {...(tried && tooShort
          ? {
              errorMessage: `Give a reason of at least ${String(DUPLICATE_OVERRIDE_REASON_MIN_LENGTH)} characters.`,
            }
          : {})}
      />
      <DialogActions>
        <Button
          variant="quiet"
          onPress={() => {
            onOpenChange(false);
          }}
        >
          Go back to the form
        </Button>
        <Button
          variant="primary"
          isPending={isPending}
          pendingLabel="Registering…"
          onPress={() => {
            setTried(true);
            if (!tooShort) onDifferent(reason.trim());
          }}
        >
          This is a different person
        </Button>
      </DialogActions>
    </Dialog>
  );
}
